import cx from 'classnames'
import { useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'

import Styles from './StartDateAndPostFrequencyModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Calendar } from 'components/Calendar/Calendar'
import { ChevronDown } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { PostingCadence } from '../../ScheduleContent'
import { Pressable } from '@react-aria/interactions'
import { ChevronRightSmall } from 'blaze-ui/icons/24'
import { Radio, RadioGroup } from 'components/RadioGroup'
import { Menu } from 'components/Menu/Menu'
import { Item } from '@react-stately/collections'
import { CustomScheduler } from './components/CustomScheduler/CustomScheduler'

export interface DayAndTime {
  day: number
  time: { ofDay: TimeOfDay; at?: { hour: number; minute: number; amOrPm: string } }
}

export enum TimeOfDay {
  EarlyMorning = 'early_morning',
  Morning = 'morning',
  Afternoon = 'afternoon',
  Evening = 'evening',
  Night = 'night',
  Random = 'random',
  Custom = 'custom',
}

export const cadenceToDisplay = (cadence: PostingCadence, startDate: Dayjs) => {
  switch (cadence) {
    case PostingCadence.Once:
      return { text: 'Post once per week', days: startDate.format('ddd') }
    case PostingCadence.Twice:
      return { text: 'Post twice per week', days: 'Tue, Thu' }
    case PostingCadence.Thrice:
      return { text: 'Post three times per week', days: 'Mon, Wed, Fri' }
    case PostingCadence.Weekdays:
      return { text: 'Post once every weekday', days: 'Mon - Fri' }
    case PostingCadence.Everyday:
      return { text: 'Post once every day', days: 'Mon - Sun' }
    case PostingCadence.Custom:
      return { text: 'Let me pick my days', days: 'Select days' }
  }
}

export const isToday = (day: Dayjs) => {
  const today = dayjs()
  return day.year() === today.year() && day.month() === today.month() && day.date() === today.date()
}

export const timeOfDayToDisplay = (timeOfDay: TimeOfDay) => {
  switch (timeOfDay) {
    case TimeOfDay.EarlyMorning:
      return { text: 'Early Morning', time: 'posts between 12am-9am' }
    case TimeOfDay.Morning:
      return { text: 'Morning', time: 'posts between 9am-12pm' }
    case TimeOfDay.Afternoon:
      return { text: 'Afternoon', time: 'posts between 12pm-5pm' }
    case TimeOfDay.Evening:
      return { text: 'Evening', time: 'posts between 5pm-9pm' }
    case TimeOfDay.Night:
      return { text: 'Night', time: 'posts between 9pm-12am' }
    case TimeOfDay.Random:
      return { text: 'Choose randomly for me', time: '' }
    case TimeOfDay.Custom:
      return { text: 'Let me choose time specifically', time: '' }
  }
}

export const firstAvailableTime = (currentHour: number): TimeOfDay => {
  if (currentHour < 11) {
    return TimeOfDay.Morning
  } else if (currentHour < 16) {
    return TimeOfDay.Afternoon
  } else if (currentHour < 20) {
    return TimeOfDay.Evening
  } else if (currentHour < 23) {
    return TimeOfDay.Night
  }

  return TimeOfDay.Morning
}

export const cadenceToDays = (cadence: PostingCadence, ofDay: TimeOfDay, startDate: Dayjs) => {
  const dayNumbersToDaysAndTimes = (days: number[]) => {
    return days.map((day) => ({ day, time: { ofDay } }))
  }

  switch (cadence) {
    case PostingCadence.Once:
      return [{ day: startDate.day(), time: { ofDay } }]
    case PostingCadence.Twice:
      return dayNumbersToDaysAndTimes([2, 4])
    case PostingCadence.Thrice:
      return dayNumbersToDaysAndTimes([1, 3, 5])
    case PostingCadence.Weekdays:
      return dayNumbersToDaysAndTimes([1, 2, 3, 4, 5])
    case PostingCadence.Everyday:
      return dayNumbersToDaysAndTimes([0, 1, 2, 3, 4, 5, 6])
    case PostingCadence.Custom:
      return []
  }
}

export const unavailableTimes = (day: number, startDate: Dayjs) => {
  let times: TimeOfDay[] = []

  if (startDate.day() !== day || !isToday(startDate)) {
    return times
  }

  const now = dayjs()

  const currentHour = now.hour()
  const currentMinute = now.minute()

  if (currentHour > 9) {
    times.push(TimeOfDay.EarlyMorning)
  }
  if (currentHour > 11) {
    times.push(TimeOfDay.Morning)
  }
  if (currentHour > 16) {
    times.push(TimeOfDay.Afternoon)
  }
  if (currentHour > 20) {
    times.push(TimeOfDay.Evening)
  }
  if (currentHour > 23 && currentMinute > 45) {
    times.push(TimeOfDay.Night)
  }
  return times
}

const currentHourIsPastWindow = (timeOfDay: TimeOfDay) => {
  const currentHour = dayjs().hour()

  switch (timeOfDay) {
    case TimeOfDay.EarlyMorning:
      return currentHour > 9
    case TimeOfDay.Morning:
      return currentHour > 12
    case TimeOfDay.Afternoon:
      return currentHour > 17
    case TimeOfDay.Evening:
      return currentHour > 21
    case TimeOfDay.Night:
      return false
  }
}

interface Props {
  initialDays: DayAndTime[]
  initialCadence: PostingCadence
  initialStartDate: Dayjs
  onScheduleChange: (schedule: { startDate: Dayjs; daysAndTimes: DayAndTime[]; isCustom: boolean }) => void
}

export const StartDateAndPostFrequencyModal = ({
  onScheduleChange,
  initialDays,
  initialCadence,
  initialStartDate,
}: Props) => {
  const { closeModal } = useModals()
  const [days, setDays] = useState<DayAndTime[]>(initialDays)
  const [postingCadence, setPostingCadence] = useState<PostingCadence | null>(initialCadence)
  const [startDate, setStartDate] = useState(initialStartDate)
  const [timeOfDay, setTimeOfDay] = useState(TimeOfDay.Random)
  const [customTime, setCustomTime] = useState<string | undefined>()
  // TODO: Fix this the next time the file is edited.
  /* eslint-disable-next-line no-nested-ternary */
  const [customAmOrPm, setCustomAmOrPm] = useState(isToday(startDate) ? (startDate.hour() < 12 ? 'am' : 'pm') : 'am')

  const applySchedule = () => {
    onScheduleChange({ startDate, daysAndTimes: days, isCustom: postingCadence === PostingCadence.Custom })
    closeModal()
  }

  const handleChangeTimeOfDay = (value: TimeOfDay) => {
    setTimeOfDay(value)
    const newDays = days.map((day) => {
      return { ...day, time: { ofDay: value } }
    })
    setDays(newDays)
  }

  return (
    <Modal.Root size="md" onPressOutside={closeModal}>
      <Modal.Header>
        <Heading level={3}>Start date & post frequency</Heading>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.content}>
          <div className={Styles.startPostingOn}>
            <Text variant="placeholder" color="var(--dark-80)">
              Start posting on
            </Text>
            <MenuTrigger placement="bottom-start">
              <Button variant="secondary" size="lg" endIcon={ChevronDown} aria-label="Start Date">
                {isToday(startDate) ? `${startDate.format('ddd, MMM D')} (Today)` : startDate.format('ddd, MMM D YYYY')}
              </Button>
              <div>
                <Calendar
                  minValue={dayjs().format('YYYY-MM-DD')}
                  value={startDate.format('YYYY-MM-DD')}
                  onChange={(value) => {
                    const newStartDate = dayjs(value)

                    if (isToday(newStartDate)) {
                      newStartDate.hour(dayjs().hour()).minute(dayjs().minute())
                      if (days.find((d) => d.day === newStartDate.day())) {
                        setTimeOfDay(firstAvailableTime(dayjs().hour()))
                      }
                      if (dayjs().hour() > 11) {
                        setCustomAmOrPm('pm')
                      }
                      let newDaysAndTimes = []
                      if (postingCadence === PostingCadence.Once) {
                        newDaysAndTimes = days.map((day) => {
                          return {
                            ...day,
                            day: newStartDate.day(),
                            time: { ...day.time, ofDay: firstAvailableTime(dayjs().hour()) },
                          }
                        })
                      } else {
                        newDaysAndTimes = days.map((day) => {
                          if (day.day === dayjs().day()) {
                            const ofDay = firstAvailableTime(dayjs().hour())
                            return { ...day, time: { ofDay } }
                          }

                          return day
                        })
                      }
                      setDays(newDaysAndTimes)
                    } else if (postingCadence === PostingCadence.Once) {
                      const newDaysAndTimes = days.map((day) => {
                        return { ...day, day: newStartDate.day() }
                      })
                      setDays(newDaysAndTimes)
                    }
                    setStartDate(newStartDate)
                  }}
                />
              </div>
            </MenuTrigger>
          </div>
          <div className={Styles.actions}>
            <div className={Styles.leftActions}>
              <Text variant="placeholder" color="var(--dark-80)">
                Select how often you want to post
              </Text>
              <div className={Styles.cadenceContainer}>
                {Object.entries(PostingCadence).map(([_key, cadence]) => {
                  const cadenceDisplay = cadenceToDisplay(cadence, startDate)
                  return (
                    <Pressable
                      onPress={() => {
                        let ofDay = timeOfDay
                        if (isToday(startDate)) {
                          ofDay = firstAvailableTime(dayjs().hour())
                          setTimeOfDay(ofDay)
                        }
                        setDays(cadenceToDays(cadence, ofDay, startDate) as DayAndTime[])
                        setPostingCadence(cadence)
                      }}
                      key={cadence}
                    >
                      <div className={cx(Styles.cadence, { [Styles.selected]: postingCadence === cadence })}>
                        <Heading level={4}>{cadenceDisplay.text}</Heading>
                        <div className={Styles.cadenceDays}>
                          <Text variant="placeholder">{cadenceDisplay.days}</Text>
                          <ChevronRightSmall color="var(--dark-40)" />
                        </div>
                      </div>
                    </Pressable>
                  )
                })}
              </div>
            </div>
            <div className={Styles.divider} />
            <div className={Styles.rightActions}>
              {postingCadence === PostingCadence.Custom ? (
                <CustomScheduler
                  title="Choose your days"
                  days={days}
                  setDays={setDays}
                  startDate={startDate}
                  customTime={customTime}
                />
              ) : (
                <>
                  <Text variant="placeholder" color="var(--dark-80)">
                    Choose your time of day
                  </Text>
                  <RadioGroup
                    aria-label="Choose your time of day"
                    value={timeOfDay}
                    onChange={(value) => handleChangeTimeOfDay(value as TimeOfDay)}
                    className={Styles.radioGroup}
                  >
                    {Object.entries(TimeOfDay).map(([key, value]) => {
                      const timeOfDayDisplay = timeOfDayToDisplay(value)

                      if (value !== TimeOfDay.Custom) {
                        return (
                          <Radio
                            key={value}
                            value={value}
                            className={Styles.radio}
                            isDisabled={
                              isToday(startDate) &&
                              days.map((d) => d.day).includes(startDate.day()) &&
                              currentHourIsPastWindow(value) &&
                              dayjs().minute() > 0
                            }
                          >
                            <div className={Styles.radioContent}>
                              <Text variant="smallList">{timeOfDayDisplay.text}</Text>
                              <Text variant="secondary">{timeOfDayDisplay.time}</Text>
                            </div>
                          </Radio>
                        )
                      }

                      return (
                        <Radio value={TimeOfDay.Custom} className={Styles.radio} key={value}>
                          <div className={Styles.radioContent}>
                            <Text variant="smallList">Let me choose time specifically</Text>
                            <div className={Styles.customControls}>
                              <MenuTrigger>
                                <Button
                                  variant="tertiary"
                                  size="xs"
                                  endIcon={ChevronDown}
                                  color="var(--dark-80)"
                                  onPress={() => {
                                    if (timeOfDay !== TimeOfDay.Custom) setTimeOfDay(TimeOfDay.Custom)
                                  }}
                                >
                                  {customTime || 'Pick time'}
                                </Button>
                                <Menu
                                  selectionMode="single"
                                  selectedKeys={[customTime as string]}
                                  onAction={(key) => {
                                    setCustomTime(key as string)
                                    const [hour, minute] = (key as string).split(':')
                                    const newDays = days.map((oldDay) => {
                                      return {
                                        ...oldDay,
                                        time: {
                                          ...oldDay.time,
                                          ofDay: TimeOfDay.Custom,
                                          at: {
                                            hour: parseInt(hour),
                                            minute: parseInt(minute),
                                            amOrPm: customAmOrPm,
                                          },
                                        },
                                      }
                                    })
                                    setDays(newDays as DayAndTime[])
                                  }}
                                >
                                  {[...Array(12)].map((num, index) =>
                                    [0, 15, 30, 45].map((minute) => {
                                      if (isToday(startDate)) {
                                        let hour = index

                                        if (customAmOrPm === 'pm') {
                                          hour = index + 12
                                        }

                                        if (dayjs().hour() > hour) return null
                                        if (dayjs().hour() === hour && dayjs().minute() > minute) return null
                                      }

                                      const time = `${index === 0 ? 12 : index}:${minute === 0 ? '00' : minute}`
                                      return (
                                        <Item key={time} textValue={time}>
                                          {time}
                                        </Item>
                                      )
                                    }),
                                  )}
                                </Menu>
                              </MenuTrigger>
                              <MenuTrigger>
                                <Button
                                  variant="tertiary"
                                  size="xs"
                                  endIcon={ChevronDown}
                                  color="var(--dark-80)"
                                  onPress={() => {
                                    if (timeOfDay !== TimeOfDay.Custom) setTimeOfDay(TimeOfDay.Custom)
                                  }}
                                >
                                  {customAmOrPm.toUpperCase()}
                                </Button>
                                <Menu
                                  selectionMode="single"
                                  selectedKeys={[customAmOrPm]}
                                  onAction={(key) => {
                                    setCustomAmOrPm(key as string)
                                    const newDays = days.map((day) => {
                                      return { ...day, time: { ...day.time, at: { ...day.time.at, amOrPm: key } } }
                                    })
                                    setDays(newDays as DayAndTime[])
                                  }}
                                >
                                  {!isToday(startDate) ||
                                    (isToday(startDate) && dayjs().hour() < 11 && <Item key="am">AM</Item>)}
                                  <Item key="pm">PM</Item>
                                </Menu>
                              </MenuTrigger>
                            </div>
                          </div>
                        </Radio>
                      )
                    })}
                  </RadioGroup>
                </>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Button size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Button>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <Button
            size="lg"
            onPress={applySchedule}
            isDisabled={
              days.length === 0 ||
              !!days.find(
                (day) =>
                  day.time.ofDay === TimeOfDay.Custom &&
                  (day.time.at?.hour === undefined || day.time.at.minute === undefined),
              )
            }
          >
            Confirm
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
